import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { trainingAPI } from "@api/TrainingAPI";
import { setTeamPresets } from "@store/actions/team.actions";
import { readVisualizerConfig } from "@mappers/notes.mapper.mjs";
import type { VisualizerConfig } from "@mappers/notes.mapper.mjs";
import { readNoteApiError } from "@utils/noteApiError.mjs";
import type { Note, NoteAttribute, NoteAttributeValue, NoteTag, NoteVisibility, NotesLoadingKey } from "../reducers/notes.reducer";

export const NOTES_SET_LOADING = "NOTES_SET_LOADING";
export const NOTES_SET_NOTES = "NOTES_SET_NOTES";
export const NOTES_ADD_NOTE = "NOTES_ADD_NOTE";
export const NOTES_REPLACE_NOTE = "NOTES_REPLACE_NOTE";
export const NOTES_REMOVE_NOTE = "NOTES_REMOVE_NOTE";
export const NOTES_SET_TAGS = "NOTES_SET_TAGS";
export const NOTES_ADD_TAG = "NOTES_ADD_TAG";
export const NOTES_REMOVE_TAG = "NOTES_REMOVE_TAG";
export const NOTES_SET_VISUALIZER_DEFAULT = "NOTES_SET_VISUALIZER_DEFAULT";
export const NOTES_SET_ATTRIBUTES = "NOTES_SET_ATTRIBUTES";
export const NOTES_ADD_ATTRIBUTE = "NOTES_ADD_ATTRIBUTE";
export const NOTES_REPLACE_ATTRIBUTE = "NOTES_REPLACE_ATTRIBUTE";
export const NOTES_REMOVE_ATTRIBUTE = "NOTES_REMOVE_ATTRIBUTE";

export interface NotePayload {
    body: string;
    player_id: number | null;
    tags: string[];
    attributes: NoteAttributeValue[];
    visibility: NoteVisibility;
    audience: string[];
}

export interface NoteAttributePayload {
    name: string;
    min_value: number | null;
    max_value: number | null;
}

// Every notes call goes through here. An error with a `field` shows next to its input,
// so the caller shows it and no toast shows. Any other error shows as a toast.
// Every error goes on to the caller.
// The trailing comma in <T,> keeps the build from reading <T> as JSX: Vite runs every file through the tsx loader.
const callNotesApi = async <T,>(call: () => Promise<T>): Promise<T> => {
    try {
        return await call();
    } catch (e) {
        const { message: text, field } = readNoteApiError(e);
        if (field === null) message.error(text);
        throw e;
    }
};

const setLoading = (key: NotesLoadingKey, value: boolean) => ({
    type: NOTES_SET_LOADING,
    payload: { key, value },
});

// The date range changes fast. Only the newest request may write to the store.
let latestNotesRequest = 0;

export const getNotes = (from: string, to: string) => async (dispatch: any) => {
    const request = ++latestNotesRequest;
    dispatch(setLoading("notes", true));
    try {
        const notes: Note[] = await callNotesApi(() => trainingAPI.getNotes(from, to));
        if (request === latestNotesRequest) dispatch({ type: NOTES_SET_NOTES, payload: notes });
    } finally {
        if (request === latestNotesRequest) dispatch(setLoading("notes", false));
    }
};

export const getNoteTags = () => async (dispatch: any) => {
    dispatch(setLoading("tags", true));
    try {
        const tags: NoteTag[] = await callNotesApi(() => trainingAPI.getNoteTags());
        dispatch({ type: NOTES_SET_TAGS, payload: tags });
    } finally {
        dispatch(setLoading("tags", false));
    }
};

export const createNoteTag = (name: string) => async (dispatch: any): Promise<NoteTag> => {
    const tag: NoteTag = await callNotesApi(() => trainingAPI.createNoteTag({ name }));
    dispatch({ type: NOTES_ADD_TAG, payload: tag });
    return tag;
};

export const deleteNoteTag = (id: string) => async (dispatch: any): Promise<void> => {
    await callNotesApi(() => trainingAPI.deleteNoteTag(id));
    dispatch({ type: NOTES_REMOVE_TAG, payload: id });
};

export const getNoteAttributes = () => async (dispatch: any): Promise<NoteAttribute[]> => {
    dispatch(setLoading("attributes", true));
    try {
        const attributes: NoteAttribute[] = await callNotesApi(() => trainingAPI.getNoteAttributes());
        dispatch({ type: NOTES_SET_ATTRIBUTES, payload: attributes });
        return attributes;
    } finally {
        dispatch(setLoading("attributes", false));
    }
};

// A save can add or remove a tag. The save has worked at this point, and `getNoteTags`
// shows its own error. The caller must not see a failed save, so the rejection ends here.
const refreshTags = (dispatch: any) => {
    dispatch(getNoteTags()).catch(() => {});
};

export const createNote = (payload: NotePayload) => async (dispatch: any): Promise<Note> => {
    const note: Note = await callNotesApi(() => trainingAPI.createNote(payload));
    dispatch({ type: NOTES_ADD_NOTE, payload: note });
    refreshTags(dispatch);
    return note;
};

export const updateNote = (uuid: string, payload: Partial<NotePayload>) => async (dispatch: any): Promise<Note> => {
    const note: Note = await callNotesApi(() => trainingAPI.updateNote(uuid, payload));
    dispatch({ type: NOTES_REPLACE_NOTE, payload: note });
    refreshTags(dispatch);
    return note;
};

export const deleteNote = (uuid: string) => async (dispatch: any): Promise<void> => {
    await callNotesApi(() => trainingAPI.deleteNote(uuid));
    dispatch({ type: NOTES_REMOVE_NOTE, payload: uuid });
    refreshTags(dispatch);
};

export const createNoteAttribute = (payload: NoteAttributePayload) => async (dispatch: any): Promise<NoteAttribute> => {
    const attribute: NoteAttribute = await callNotesApi(() => trainingAPI.createNoteAttribute(payload));
    dispatch({ type: NOTES_ADD_ATTRIBUTE, payload: attribute });
    return attribute;
};

export const updateNoteAttribute = (id: number, payload: Partial<NoteAttributePayload>) => async (dispatch: any): Promise<NoteAttribute> => {
    const attribute: NoteAttribute = await callNotesApi(() => trainingAPI.updateNoteAttribute(id, payload));
    dispatch({ type: NOTES_REPLACE_ATTRIBUTE, payload: attribute });
    return attribute;
};

export const deleteNoteAttribute = (id: number) => async (dispatch: any): Promise<void> => {
    await callNotesApi(() => trainingAPI.deleteNoteAttribute(id));
    dispatch({ type: NOTES_REMOVE_ATTRIBUTE, payload: id });
};

// `PresetSelection` creates, selects, and deletes presets. This saves a new config over a preset.
// `PresetSelection` does not wait for the save, so the error shows here and ends here.
export const saveNoteVisualizerPreset =
    (preset: { preset_id: number; name: string }, config: VisualizerConfig) => async (dispatch: any, getState: any) => {
        try {
            await trainingAPI.updatePreset({ preset_id: preset.preset_id, name: preset.name, type: "note-visualizer", config });
        } catch {
            message.error("The preset could not be saved. Please try again.");
            return;
        }
        const presets = getState().team.presets.map((p: any) => (p.preset_id === preset.preset_id ? { ...p, config } : p));
        dispatch(setTeamPresets(presets));
        message.success("Preset saved.");
    };

// The team default lives with the default stat tiles of the other tabs, under the `notes` key.
// A team with no saved default gets the built-in config.
export const getNoteVisualizerDefault = () => async (dispatch: any) => {
    try {
        const stats = await trainingAPI.getDefaultStats();
        dispatch({ type: NOTES_SET_VISUALIZER_DEFAULT, payload: readVisualizerConfig(stats?.notes) });
    } catch {
        message.error("The default columns could not be loaded. Please try again.");
    }
};

export const saveNoteVisualizerDefault = (config: VisualizerConfig) => async (dispatch: any) => {
    try {
        await trainingAPI.updateDefaultStats(config, "notes");
    } catch {
        message.error("The default could not be saved. Please try again.");
        return;
    }
    dispatch({ type: NOTES_SET_VISUALIZER_DEFAULT, payload: config });
    message.success("Default saved.");
};
